import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import { QUERY_KEYS } from "../../constants/QueryKeys";
import { isUnreachable } from "../../models/ConfiguredSourceRoot";
import { MigrationType } from "../../models/Statistics/MigrationType";
import SourceLinksRepository from "../../repositories/SourceLinksRepository";
import TableEmptyState from "../common/TableEmptyState";
import ConfiguredSourceRootsTable from "./ConfiguredSourceRootsTable";

/** The discoveries whose roots belong under one entity-type tab: the type's own, plus the folder discovery it shares a parent with. */
const discoveriesOf = (entityType: MigrationType): MigrationType[] => {
  switch (entityType) {
    case MigrationType.CasesMigration:
      return [MigrationType.CasesMigration, MigrationType.CaseFoldersMigration];
    case MigrationType.ProvidersMigration:
      return [MigrationType.ProvidersMigration, MigrationType.ProviderFoldersMigration];
    default:
      return [entityType];
  }
};

/**
 * The configured roots of the selected type — the paths discovery enumerates entities out of. They are the
 * story's "root folder paths", and they live in their own registry rather than as link rows: an unreadable root
 * enumerates no entities, so there would be no links to mark.
 */
export const SourceRootsSection: React.FC<{ organizationId: string; entityType: MigrationType }> = ({
  organizationId,
  entityType,
}) => {
  const repository = new SourceLinksRepository();
  const [open, setOpen] = React.useState(false);
  const discoveries = discoveriesOf(entityType);

  const rootsQuery = useQuery({
    queryKey: [QUERY_KEYS.CONFIGURED_SOURCE_ROOTS, organizationId, "by-type", entityType],
    queryFn: async () => {
      const perDiscovery = await Promise.all(
        discoveries.map((migrationType) => repository.getConfiguredSourceRoots({ migrationType }))
      );
      return perDiscovery.flat();
    },
    enabled: !!organizationId,
  });

  const roots = rootsQuery.data ?? [];
  const unreachable = roots.filter(isUnreachable).length;

  // Absent rather than empty when nothing reported a root. Only the folder discoveries enumerate from a path, so
  // on a type whose entities come from the source database — intakes, templates — the section could never fill,
  // and a header reading "0 configured" invites the reader to go looking for configuration that does not exist.
  // Nothing is hidden by this: a root that failed is still a root row, with its outcome set to unreachable, so a
  // broken root leaves the section populated rather than empty.
  if (roots.length === 0 && !rootsQuery.isError) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between px-4 py-2 h-auto">
          <span className="flex items-center gap-2">
            <span className="font-medium">Configured roots</span>
            <span className="text-sm text-muted-foreground">{roots.length} configured</span>
            {unreachable > 0 && (
              <Badge variant="danger">
                <AlertTriangle className="size-3" />
                {unreachable} unreachable
              </Badge>
            )}
          </span>
          <ChevronDown className={open ? "size-4 rotate-180 transition-transform" : "size-4 transition-transform"} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t">
          {rootsQuery.isError ? (
            <TableEmptyState icon={AlertTriangle} title="Could not load roots" description="The configured source roots could not be fetched." />
          ) : (
            <div className="min-w-0 overflow-auto">
              <ConfiguredSourceRootsTable roots={roots} />
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default SourceRootsSection;
